import React, { Dispatch, FormEvent, SetStateAction, useEffect } from 'react';
import { UseBoolean } from 'react-hanger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GridSettingsMenuItem from '@components-clean/core/grid/grid-menu/grid-settings-menu/grid-settings-menu-item';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import { useBoolean, useTranslation } from '@hooks/index';

interface GridVisibilityMenuProps {
    columns: GridColumnProps[];
    updateColumns: Dispatch<SetStateAction<GridColumnProps[]>>;
    menuExpand: UseBoolean;
    closeSettingsMenu?: () => void;
    hiddenColumnsUpdated: ReturnType<typeof useBoolean>;
}

const getVisibilityColumns = (columns: GridColumnProps[]) => {
    const visibilityColumns: GridColumnProps[] = [];

    columns.forEach((column) => {
        // NOTE: 'field' is a unique property and only regular columns have it (Multi-Column Headers don't have it)
        if (column.field && !column.lockVisibility) {
            visibilityColumns.push(column);
        }
    });

    return visibilityColumns;
};

const resetVisibilityState = (columns: GridColumnProps[] | undefined): GridColumnProps[] | undefined => {
    if (!columns) {
        return undefined;
    }

    return columns.map((column) => ({
        ...column,
        show: column.showByDefault ?? true,
        children: resetVisibilityState(column.children),
    }));
};

const updateVisibilityState = (
    mainColumns: GridColumnProps[],
    newColumns: GridColumnProps[],
    parentColumnVisibility?: boolean,
    newColumnsIdx?: number,
    enhancedGroupedColumnToggling?: boolean,
): GridColumnProps[] =>
    mainColumns.map((column, idx) => {
        let showColumn = parentColumnVisibility === false ? false : column.showByDefault;

        if (column.field && (parentColumnVisibility !== false || enhancedGroupedColumnToggling)) {
            if (newColumnsIdx !== undefined) {
                if (enhancedGroupedColumnToggling)
                    showColumn =
                        newColumns[newColumnsIdx].children!.find((item) => item.field === column.field)?.show ??
                        column.showByDefault;
                else {
                    // For non-enhanced grouped columns, child col visibility is controlled by the parent
                    showColumn = parentColumnVisibility ?? false;
                }
            } else showColumn = newColumns.find((item) => item.field === column.field)?.show ?? column.showByDefault;
        }

        const updatedChildren =
            column.children &&
            updateVisibilityState(column.children, newColumns, showColumn, idx, column.enhancedGroupedColumnToggling);

        // Enhanced Grouped column parents cannot be toggled off, they are instead controlled by children visibility
        if (enhancedGroupedColumnToggling && updatedChildren && updatedChildren?.length > 0)
            if (updatedChildren.find((item) => item.show)) showColumn = true;

        return {
            ...column,
            show: showColumn,
            children: updatedChildren,
        };
    });

// NOTE: Hide the parent column when all children are hidden
const updateParentColumnVisibility = (columns: GridColumnProps[]): GridColumnProps[] =>
    columns.map((column) => {
        const isChildrenShown = !!column.children?.filter((child) => child.show).length;

        return {
            ...column,
            show: column.children?.length ? isChildrenShown : column.show,
            children: column.children?.length ? updateParentColumnVisibility(column.children) : undefined,
        };
    });

const GridVisibilityMenu = ({
    columns,
    updateColumns,
    closeSettingsMenu,
    menuExpand,
    hiddenColumnsUpdated,
}: GridVisibilityMenuProps) => {
    const { t } = useTranslation();
    const [currentColumns, setCurrentColumns] = React.useState<GridColumnProps[]>(getVisibilityColumns(columns));
    useEffect(() => {
        if (!menuExpand.value) {
            getVisibilityColumns(columns);
        }
    }, [menuExpand.value]);

    const onToggleColumnVisibility = (id: number) => {
        setCurrentColumns(
            currentColumns.map((column, idx) => ({
                ...column,
                show: idx === id ? !column.show : column.show,
            })),
        );
    };

    const onToggleChildColumnVisibility = (id: number, childId: number) => {
        setCurrentColumns(
            currentColumns.map((column, idx) =>
                id === idx
                    ? {
                          ...column,
                          children: column.children
                              ? [
                                    ...column.children.map((child, childIdx) =>
                                        childIdx === childId ? { ...child, show: !child.show } : child,
                                    ),
                                ]
                              : undefined,
                      }
                    : column,
            ),
        );
    };

    const onReset = (event: FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        const newColumns = resetVisibilityState(columns) ?? [];

        setCurrentColumns(getVisibilityColumns(newColumns));
        updateColumns(newColumns);
        hiddenColumnsUpdated.setTrue();
        if (closeSettingsMenu) {
            closeSettingsMenu();
        }
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        let newColumns = updateVisibilityState(columns, currentColumns);
        newColumns = updateParentColumnVisibility(newColumns);
        updateColumns(newColumns);
        hiddenColumnsUpdated.setTrue();
        if (closeSettingsMenu) {
            closeSettingsMenu();
        }
    };

    return (
        <GridSettingsMenuItem
            columns={currentColumns}
            onSubmit={onSubmit}
            onToggle={onToggleColumnVisibility}
            onToggleChild={onToggleChildColumnVisibility}
            onReset={onReset}
            fieldName="show"
            buttonProps={{
                icon: <FontAwesomeIcon icon={['far', 'eye-slash']} fontSize={15} />,
                label: `${t('showHideColumns')}`,
            }}
            menuExpand={menuExpand}
            type="show"
            atLeastOneSelectionRequired
        />
    );
};

export default GridVisibilityMenu;
